import React, { type FC } from "react";
import classNames from "classnames";
import type { ApiDivision } from "@src/api/dictionaries/divisions";
import "./BusinessDivisionValue.scss";

/** Минимальный контракт подразделения / участка-как-подразделения для отображения. */
export type BusinessDivisionValueDivision = Pick<ApiDivision, "Sign" | "Name" | "WSCode">;

/**
 * Участок для отображения после `/`. \
 * Тот же контракт, что у подразделения (Sign→Name→WSCode).
 */
export type BusinessDivisionValueSection = BusinessDivisionValueDivision;

/** Пропсы компонента {@link BusinessDivisionValue}. */
export interface BusinessDivisionValueProps {
    /**
     * Подразделение. \
     * Основной текст: `Sign` как есть; иначе `(наим.: Name)`; иначе `(цех.код: WSCode)`; иначе «Безымянное подразделение».
     */
    division?: BusinessDivisionValueDivision | null;
    /**
     * Участок (производственное подразделение); отображается как уточнение после `/` \
     * теми же правилами Sign→Name→WSCode.
     */
    section?: BusinessDivisionValueSection | null;
    /** Текст или узел, если нет ни подразделения, ни участка. @default "Не задано" */
    fallback?: React.ReactNode;
    /** Дополнительный CSS-класс корневого контейнера. */
    className?: string;
}

/** Подпись, когда объект подразделения есть, но `Sign` / `Name` / `WSCode` пусты. */
const UNNAMED_DIVISION_LABEL = "«Безымянное подразделение»";

function resolveTextPart(value: string | null | undefined): string | null {
    const text = value?.trim();
    return text === "" ? null : (text ?? null);
}

/**
 * Основной текст подразделения по приоритету полей.
 * @param division подразделение
 * @returns `Sign` → `(наим.: Name)` → `(цех.код: WSCode)` → «Безымянное подразделение»; `null`, если объекта нет
 */
function resolveDivisionPrimaryText(division?: BusinessDivisionValueDivision | null): string | null {
    if (division == null) {
        return null;
    }

    const sign = resolveTextPart(division.Sign);
    if (sign != null) {
        return sign;
    }

    const name = resolveTextPart(division.Name);
    if (name != null) {
        return `(наим.: ${name})`;
    }

    const wsCode = resolveTextPart(division.WSCode);
    if (wsCode != null) {
        return `(цех.код: ${wsCode})`;
    }

    return UNNAMED_DIVISION_LABEL;
}

/**
 * Формирует plain-text представление подразделения и участка.
 * @param division подразделение (`Sign` → `(наим.: Name)` → `(цех.код: WSCode)` → «Безымянное подразделение»)
 * @param section участок как подразделение (те же правила)
 * @returns строка вида `Ц01 / Участок` или пустая строка, если данных нет
 */
export function formatDivisionText(
    division?: BusinessDivisionValueDivision | null,
    section?: BusinessDivisionValueSection | null,
): string {
    const primary = resolveDivisionPrimaryText(division);
    const sectionName = resolveDivisionPrimaryText(section);

    if (primary == null) {
        return sectionName ?? "";
    }

    if (sectionName == null) {
        return primary;
    }

    return `${primary} / ${sectionName}`;
}

/**
 * Read-only отображение подразделения с участком. \
 * Оба уровня — Sign→Name→WSCode; участок вторичным тоном после `/`.
 */
export const BusinessDivisionValue: FC<BusinessDivisionValueProps> = ({
    division,
    section,
    fallback = "Не задано",
    className,
}) => {
    const primary = resolveDivisionPrimaryText(division);
    const sectionName = resolveDivisionPrimaryText(section);

    if (primary == null && sectionName == null) {
        return <span className={className}>{fallback}</span>;
    }

    if (primary == null) {
        return <span className={className}>{sectionName}</span>;
    }

    return (
        <span className={classNames("business_division_value", className)}>
            <span className="business_division_value__sign">{primary}</span>
            {sectionName != null && <span className="business_division_value__section">/ {sectionName}</span>}
        </span>
    );
};
